import { Calendar, ChevronRight, Confetti, Earth, Envelope, Telephone } from "@mynaui/icons-react";
import { useEffect, useState } from "react";
import moment from "moment";
import { ICoach } from "../../models/ICoach";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, EmptyState, PageHeader, TableWrap, Tabs, Tag } from "../../ui";
import { PersonMeta, PersonProfile, ProfileLine } from "../PlayerDetail/PersonProfile";
import "./styles.scss";

type CoachDetailProps = {
  item: ICoach;
  onBack: (count: number) => void;
};

const PLAYERS_COLUMNS = [{ label: "name" }, { label: "nationality" }, { label: "phone number" }, { label: "email" }];
const TEAMS_COLUMNS = [{ label: "team name" }, { label: "tournament" }, { label: "category" }];

const TAB_OPTIONS: { value: "teams" | "players"; label: string }[] = [
  { value: "players", label: "Players" },
  { value: "teams", label: "Teams" },
];

const CoachDetail = ({ item, onBack }: CoachDetailProps) => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"teams" | "players">("players");

  const handleTabClick = (tab: "teams" | "players") => {
    setActiveTab(tab);

    const count = searchParams.get("count");

    searchParams.set("tab", tab);
    searchParams.set("count", `${Number(count) + 1}`);

    setSearchParams(searchParams);
  };

  const handleBack = () => {
    const count = searchParams.get("count");
    onBack(Number(count));
  };

  useEffect(() => {
    const tab = searchParams.get("tab") as "teams" | "players" | null;
    if (tab) {
      setActiveTab(tab);
    }
  }, []);

  const fullName = `${item.firstName} ${item.lastName ?? ""}`;

  const contacts: ProfileLine[] = [{ key: "email", icon: <Envelope size={20} />, content: item.email }];
  if (item.phoneNumber) contacts.push({ key: "phone", icon: <Telephone size={20} />, content: item.phoneNumber });

  const facts: ProfileLine[] = [];
  if (item.birthDate)
    facts.push({ key: "born", icon: <Confetti size={20} />, content: `Born ${moment(item.birthDate).format("DD MMM YYYY")}` });
  if (item.languages.length > 0)
    facts.push({ key: "speaks", icon: <Earth size={20} />, content: `Speaks ${item.languages.map((x) => x.name).join(", ")}` });
  facts.push({ key: "joined", icon: <Calendar size={20} />, content: `Joined ${moment(item.createdAt).format("MMM YYYY")}` });

  const openCell = (
    <td className="ui-table__actions">
      <ChevronRight size={20} className="coach-detail__chevron" />
    </td>
  );

  return (
    <div className="coach-detail">
      <PageHeader
        back={{ onClick: handleBack, label: "Back" }}
        title={fullName}
        subtitle={<PersonMeta birthDate={item.birthDate} gender={item.gender} nationality={item.nationality} />}
      >
        <Tag tone={item.emailVerified ? "ok" : "warn"}>{item.emailVerified ? "Verified" : "Unverified"}</Tag>
      </PageHeader>

      <PersonProfile name={fullName} photo={item.photo} contacts={contacts} facts={facts} />

      <div className="coach-detail__data">
        <Tabs options={TAB_OPTIONS} value={activeTab} onChange={handleTabClick} ariaLabel="Representer sections" />
        <div className="coach-detail__tab-content">
          {(item.players.length > 0 && activeTab === "players") || (item.teams.length > 0 && activeTab === "teams") ? (
            <TableWrap>
              <table className="ui-table">
                <thead>
                  <tr>
                    {(activeTab === "players" ? PLAYERS_COLUMNS : activeTab === "teams" ? TEAMS_COLUMNS : []).map(
                      (item, id) => (
                        <th key={id}>{item.label}</th>
                      )
                    )}
                    <th className="ui-table__actions" />
                  </tr>
                </thead>
                <tbody>
                  {(activeTab === "players" ? item.players : activeTab === "teams" ? item.teams : []).map(
                    (row, rowIndex) => {
                      if (activeTab === "players") {
                        const player = row as (typeof item.players)[number];
                        return (
                          <tr
                            key={rowIndex}
                            className="is-clickable"
                            onClick={() => navigate(`/dashboard/players/${player.id}`)}
                          >
                            <td className="ui-table__primary">
                              {player.firstName} {player.lastName ?? ""}
                            </td>
                            <td>{player.nationality}</td>
                            <td>{player.phoneNumber ?? "-"}</td>
                            <td>{player.email}</td>
                            {openCell}
                          </tr>
                        );
                      }
                      if (activeTab === "teams") {
                        const team = row as ReturnType<any>[number];
                        return (
                          <tr
                            key={rowIndex}
                            className="is-clickable"
                            onClick={() =>
                              navigate(
                                `/dashboard/tournaments/${team.tournamentId}/${team.tournamentCategoryId}/teams/${team.teamId}`
                              )
                            }
                          >
                            <td className="ui-table__primary">{team.teamName}</td>
                            <td>{team.tournamentName}</td>
                            <td>{team.categoryName}</td>
                            {openCell}
                          </tr>
                        );
                      }
                      return null;
                    }
                  )}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <Card padding="none">
              <EmptyState
                title={`No ${activeTab}`}
                description={`This representer currently has no ${activeTab} ${activeTab !== "players" ? "history" : ""}`}
              />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachDetail;
